import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ButtonClick, MediaService } from '@se/se.ui.html5';
import { EcgConfiguration } from '../../app/resolvers/ecg-configuration';
import { EcgChartComponent } from '../ecg-chart/ecg-chart.component';
import { SchillerFilters } from '../ecg-fab-tools/layout-fab-tools/layout-fab-tools.component';
import { ICustomMeasureButton } from '../measures/caliper-measures/caliper-measures.component';
import { ICustomMeasure } from '../models/iannotation';
import { EcgState } from '../redux/ecgstate';
import { EcgMeasuresService } from '../utils/ecg-measures.service';
import { ChartViewModel } from '../view-models/chart-viewmodel';
import { ArrangementConfiguration } from '../view-models/trace-viewmodel';

export interface CustomDialog {
    object: string;
    visible: boolean;
    title: string;
    description: string;
    payload?: any;
    buttonCancel?: boolean;
    buttonOk?: boolean;
}

const DEFAULT_DIALOG: CustomDialog = {
    object: '',
    visible: false,
    title: 'Warning',
    description: 'WarningExaminationModified',
    buttonCancel: false,
    buttonOk: true,
};

@Component({
    selector: 'ecg-viewer',
    templateUrl: './ecg-viewer.component.html',
    styleUrls: ['./ecg-viewer.component.scss'],
})
export class EcgViewerComponent implements OnChanges {
    caliperActivated = new EventEmitter<boolean>();
    @ViewChild(EcgChartComponent) ecgChart?: EcgChartComponent;

    @Input() chart!: ChartViewModel;
    @Input() customMeasureButtons: ICustomMeasureButton[] = new Array<ICustomMeasureButton>();
    @Input() customMeasures!: Array<ICustomMeasure>;
    @Input() ecgState = new EcgState();
    @Input() enableMeasureOptions = true;
    @Input() ecgConfiguration!: EcgConfiguration;
    @Input() readOnly = false;
    @Input() enableMeasuresOnlyAtFullscreen = false;
    @Input() hideWhileCentering: boolean = true;
    @Input() reportingWithoutTrace = false;
    @Input() sopInstanceUID!: string;

    @Input() schillerFilters!: SchillerFilters;
    @Input() printAsDisplayed!: boolean;
    @Input() chartTitle!: string;

    @Input() showGlobalMeasures: boolean = false;

    @Output() ecgConfigurationChange = new EventEmitter<EcgConfiguration>();
    @Output() lowFilterChange: EventEmitter<number> = new EventEmitter<number>();

    @Output() customMeasuresChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    isFabVisible = false;
    fullscreen = false;

    showCustomMeasures = false;
    showGlobalAnnotations = false;
    showLeadsAnnotations = false;
    customDialog = DEFAULT_DIALOG;

    constructor(
        public mediaService: MediaService,
        private ecgMeasuresService: EcgMeasuresService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.customMeasures) {
            this.ecgMeasuresService.updateMeasures(changes.customMeasures.currentValue);
            this.showLeadsAnnotations = false;
        }
        if (changes.schillerFilters) {
            this.checkOldFilter(changes.schillerFilters);
        }
    }

    public onCustomDialog(button: ButtonClick) {
        if (button === ButtonClick.ok) {
            switch (this.customDialog.object) {
                case 'filter':
                    this.customDialog.visible = false;
                    break;
            }
        }
    }

    get showCaliperAndMeasures(): boolean {
        if (!this.enableMeasuresOnlyAtFullscreen) return this.canShowCustomMeasures() || this.canShowGlobalMeasures();

        return this.fullscreen && (this.canShowCustomMeasures() || this.canShowGlobalMeasures());
    }

    get showCustomMeasuresButton(): boolean {
        if (!this.chart || !this.chart.activeLayer || !this.chart.activeLayer.ecgData) return false;

        return this.customMeasures && this.customMeasures.length > 0;
    }

    get showGlobalAnnotationsButton(): boolean {
        if (!this.chart || !this.chart.activeLayer || !this.chart.activeLayer.ecgData) return false;

        return this.chart.activeLayer.ecgData.originalWaveform.annotations.length > 0;
    }

    get showLeadsAnnotationsButton(): boolean {
        if (!this.chart || !this.chart.activeLayer || !this.chart.activeLayer.ecgData) return false;

        if (
            this.chart.activeLayer.ecgData.derivedWaveform.channels == null ||
            this.chart.activeLayer.ecgData.derivedWaveform.channels.length == 0
        )
            return false;

        return (
            this.chart.activeLayer.ecgData.derivedWaveform.channels
                .map((channel) => (channel.annotations ? channel.annotations.length : 0))
                .reduce((total, amount) => total + amount) > 0
        );
    }

    private _arrangementConfiguration: ArrangementConfiguration | null = null;
    get arrangementConfiguration(): ArrangementConfiguration | null {
        this.updateArrangementConfiguration();
        return this._arrangementConfiguration;
    }

    // public functions

    onCloseCustomMeasures() {
        this.onCaliperActivate(false);
    }

    onCaliperActivate(active: boolean) {
        this.showCustomMeasures = active;
        this.showGlobalAnnotations = active;
    }

    onFullscreenChange() {
        this.fullscreen = !this.fullscreen;
        this.ecgChart?.fit();
    }

    onLowFilterChange(event: number) {
        this.lowFilterChange.emit(event);
    }

    onCustomMeasuresChange(active: boolean) {
        this.showCustomMeasures = active;
        this.ecgChart?.fit();
        this.customMeasuresChange.emit(this.showCustomMeasures);
    }

    onGlobalAnnotationsChange() {
        this.showGlobalAnnotations = !this.showGlobalAnnotations;
        this.ecgChart?.fit();
    }

    onLeadsAnnotationsChange(active: boolean) {
        this.showLeadsAnnotations = active;
        this.ecgChart?.fit();
    }

    canShowGlobalMeasures(): boolean {
        return (
            !!this.showGlobalAnnotations &&
            !!this.chart &&
            !!this.chart.activeLayer &&
            !!this.chart.activeLayer.ecgData &&
            !!this.chart.activeLayer.ecgData.originalWaveform.annotations &&
            this.chart.activeLayer.ecgData.originalWaveform.annotations.length > 0
        );
    }

    canShowCustomMeasures(): boolean {
        return this.showCustomMeasures && this.customMeasures && this.customMeasures.length > 0;
    }

    canShowCaliperMeasures(): boolean {
        return this.showCustomMeasures && !!this.ecgChart && this.ecgChart.measuresSummary.visible;
    }

    // private functions

    private checkOldFilter(filters: SimpleChange) {
        let displayDialog = false;
        let cValue = filters.currentValue;
        this.customDialog.visible = false;
        if (cValue && cValue.lowFilter !== undefined && cValue.highFilter !== undefined) {
            if (cValue.lowFilter === -1 || cValue.highFilter === -1) {
                // ECG & Visita + ECG
                displayDialog = true;
            }
            if (displayDialog) {
                this.customDialog.description = 'ECG_OldFiltersWarning';
                this.customDialog.object = 'filter';
                this.customDialog.title = 'Warning';
                this.customDialog.visible = true;
            }
        }
    }

    private updateArrangementConfiguration() {
        if (!this.chart || !this.chart.activeLayer || this.chart.activeLayer.traces.length === 0) {
            this._arrangementConfiguration = null;
        } else {
            if (!this._arrangementConfiguration) {
                this._arrangementConfiguration = new ArrangementConfiguration();
            }

            this._arrangementConfiguration.leadCount = this.chart.activeLayer.leadCount;
            this._arrangementConfiguration.derivedWaveformPresent =
                !!this.chart.activeLayer.traces?.[0]?.channelSelector?.derivedChannel;
        }
    }
}
